import { DateTime } from "luxon";
import type { StateStep } from "../../apiClient/generated";

/**
 * Labels and step classification for the data import state machine, see
 * docs/docs/data-import-pipeline.md.
 */

export const TERMINAL_STATES = ["COMPLETE", "ERROR", "TIMED_OUT"];

const IMPORT_TYPE_LABELS: Record<string, string> = {
    EMISSIONS: "Emissions",
    INFRASTRUCTURE: "Infrastructure",
    SCENES: "Scenes",
    EPA_SEP: "EPA SEP",
    NON_DETECTS: "Non-detects",
    EXTRA_DATA: "Extra data",
};

// INFRASTRUCTURE imports branch on import_params.data_type.
const DATA_TYPE_LABELS: Record<string, string> = {
    Infrastructure: "Sites",
    AerialImages: "Aerial images",
    Pipelines: "Pipelines",
};

const ACTION_STATUS_LABELS: Record<string, string> = {
    QUEUED: "Queued",
    RUNNING: "Running",
    SUCCEEDED: "Succeeded",
    FAILED: "Failed",
};

/** "PROCESSING_NON_DETECTS" -> "Processing non-detects". */
export const humanizeCode = (code: string): string => {
    const words = code
        .replaceAll("NON_DETECTS", "NON-DETECTS")
        .toLowerCase()
        .split("_")
        .filter(Boolean)
        .join(" ");
    return words.charAt(0).toUpperCase() + words.slice(1);
};

/** "data_provider" -> "Data provider". */
export const humanizeKey = (key: string): string => {
    const words = key.replaceAll("_", " ").trim();
    return words.charAt(0).toUpperCase() + words.slice(1);
};

export const importTypeLabel = (importType: string) =>
    IMPORT_TYPE_LABELS[importType] ?? humanizeCode(importType);

/** The infrastructure data type, the branch an INFRASTRUCTURE import takes. */
export const dataTypeLabel = (importParams: unknown): string | null => {
    const dataType = paramValue(importParams, "data_type");
    if (dataType === null) {
        return null;
    }
    return DATA_TYPE_LABELS[dataType] ?? dataType;
};

export const providerLabel = (importParams: unknown): string | null =>
    paramValue(importParams, "data_provider");

const paramValue = (importParams: unknown, key: string): string | null => {
    if (!importParams || typeof importParams !== "object") {
        return null;
    }
    const value = (importParams as Record<string, unknown>)[key];
    return value === undefined || value === null || value === ""
        ? null
        : String(value);
};

export const stateLabel = (state: string) => humanizeCode(state);

/** Action status label, or null when the step has no action. */
const actionStatusLabel = (actionStatus: string | null | undefined) =>
    actionStatus ? (ACTION_STATUS_LABELS[actionStatus] ?? actionStatus) : null;

/**
 * "Action running", or "Waiting" for a running step whose action is done: it
 * queued work (images, matching runs) and the step waits for that to finish.
 */
export const actionText = (
    actionStatus: string | null | undefined,
    isRunningStep: boolean,
) => {
    if (isRunningStep && actionStatus === "SUCCEEDED") {
        return "Waiting";
    }
    const label = actionStatusLabel(
        actionStatus === "NONE" ? null : actionStatus,
    );
    return label ? `Action ${label.toLowerCase()}` : null;
};

export const isTerminal = (state: string | null | undefined) =>
    !!state && TERMINAL_STATES.includes(state);

/** "<1 s", "1.6 s", "25 s", "3 min 20 s", "2 h 5 min", "1 d 3 h". */
export const formatDuration = (seconds: number | null | undefined): string => {
    if (seconds === null || seconds === undefined || seconds < 0) {
        return "—";
    }
    if (seconds < 1) {
        return "<1 s";
    }
    if (seconds < 10) {
        return `${seconds.toFixed(1)} s`;
    }
    const total = Math.round(seconds);
    if (total < 60) {
        return `${total} s`;
    }
    const days = Math.floor(total / 86400);
    const hours = Math.floor((total % 86400) / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const secs = total % 60;
    if (days > 0) {
        return hours > 0 ? `${days} d ${hours} h` : `${days} d`;
    }
    if (hours > 0) {
        return minutes > 0 ? `${hours} h ${minutes} min` : `${hours} h`;
    }
    return secs > 0 ? `${minutes} min ${secs} s` : `${minutes} min`;
};

export const secondsBetween = (from: Date, to: Date) =>
    (to.getTime() - from.getTime()) / 1000;

/**
 * A step's start time. The date is only shown when it differs from the
 * reference, since an import's steps usually all run on the same day.
 */
export const formatStepTime = (date: Date | null, reference?: Date | null) => {
    if (!date) {
        return "—";
    }
    const dt = DateTime.fromJSDate(date);
    const sameDay =
        reference && DateTime.fromJSDate(reference).hasSame(dt, "day");
    return dt.toFormat(sameDay ? "HH:mm:ss" : "LLL d, HH:mm:ss");
};

export type StepProblem = "failed" | "timedOut";

export interface PipelineStep extends StateStep {
    isCurrent: boolean;
    isTerminal: boolean;
    // Where the import failed or timed out
    problem: StepProblem | null;
}

/**
 * The steps, each marked as current, or as where the import failed or timed
 * out.
 *
 * An import that ends in ERROR failed in the step before it, whether that
 * step's action failed or its condition sent it to ERROR (e.g. a failed
 * matching run). One that ends in TIMED_OUT was stuck in the step before it.
 */
export const pipelineSteps = (steps: StateStep[]): PipelineStep[] => {
    const last = steps.length - 1;
    const finalState = steps[last]?.state;

    return steps.map((step, index) => {
        let problem: StepProblem | null = null;
        if (index === last - 1 && finalState === "TIMED_OUT") {
            problem = "timedOut";
        } else if (
            (index === last - 1 && finalState === "ERROR") ||
            step.actionStatus === "FAILED"
        ) {
            problem = "failed";
        }
        return {
            ...step,
            isCurrent: index === last,
            isTerminal: isTerminal(step.state),
            problem,
        };
    });
};

/**
 * How long the whole pipeline took, or has been running.
 */
export const pipelineDuration = (
    steps: StateStep[],
    now: Date = new Date(),
): { seconds: number; running: boolean } | null => {
    const first = steps[0]?.enteredAt;
    const last = steps[steps.length - 1];
    if (!first || !last) {
        return null;
    }
    if (isTerminal(last.state)) {
        return last.enteredAt
            ? { seconds: secondsBetween(first, last.enteredAt), running: false }
            : null;
    }
    return { seconds: secondsBetween(first, now), running: true };
};

export interface ImportErrorSummary {
    // The step the error was recorded in
    state: string | null;
    // How the stuck step's action ended, for timeouts
    actionStatus: string | null;
    errors: unknown;
}

/**
 * Splits import_errors into the step it names and the errors themselves.
 *
 * The state machine writes {"errors": ..., "state": <step>} when a step
 * fails, and {"errors": ..., "action_status": ...} on a timeout. Legacy
 * imports only have "errors". Anything else is shown as is.
 */
export const summarizeImportErrors = (
    importErrors: unknown,
): ImportErrorSummary | null => {
    if (importErrors === null || importErrors === undefined) {
        return null;
    }
    if (
        typeof importErrors === "object" &&
        !Array.isArray(importErrors) &&
        "errors" in importErrors
    ) {
        const { errors, state, action_status } = importErrors as Record<
            string,
            unknown
        >;
        return {
            state: typeof state === "string" ? state : null,
            actionStatus:
                typeof action_status === "string" ? action_status : null,
            errors,
        };
    }
    return { state: null, actionStatus: null, errors: importErrors };
};

/** What a timed out step's action status says about why it got stuck. */
export const timeoutHint = (actionStatus: string | null) => {
    if (actionStatus === "QUEUED") {
        return "Its action was never picked up by a worker.";
    }
    if (actionStatus === "RUNNING") {
        return "Its action hung, or its worker died.";
    }
    return null;
};
